import styles from './MarketplacePage.module.css';

import React, { useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import UiBlocker from 'components/bng/ui/UiBlocker';
import Api from 'components/Api';
import useBimContext from 'components/hooks/useBimContext';
import MktHomeTab from 'components/bng/pages/marketplace/tabs/MktHomeTab';
import MktPlanTab from 'components/bng/pages/marketplace/tabs/MktPlanTab';
import MktAdditionalsTab from 'components/bng/pages/marketplace/tabs/MktAdditionalsTab';
import MktAddonsTab from 'components/bng/pages/marketplace/tabs/MktAddonsTab';
import MktServicesTab from 'components/bng/pages/marketplace/tabs/MktServicesTab';
import MktTemplatesTab from 'components/bng/pages/marketplace/tabs/MktTemplatesTab';
import MktEducationalTab from 'components/bng/pages/marketplace/tabs/MktEducationalTab';
import useFetchData from 'components/hooks/useFetchData';
import Utils from 'components/Utils';
import useBimNavigate from 'components/hooks/useBimNavigate';
import { CheckoutPage } from 'components/bng/pages/marketplace/Checkout';
import { MarketplaceItemTab } from 'components/bng/pages/marketplace/MarketplaceItemPage';
import { MarketplaceTemplateItemTab } from 'components/bng/pages/marketplace/MarketplaceTemplateItemPage';
import UiMsg from 'components/ui/UiMsg';
import NavPanel from 'components/ui/accounts/NavPanel';

export const MKT_ACC_QUERY_PARAM = 'currentAccount';

export const MARKETPLACE_GROUPS = {
  BRAND: 'BRAND',
  SECURITY: 'SECURITY',
  MANAGEMENT: 'MANAGEMENT',
  TEMPLATE: 'TEMPLATE',
};

const MARKETPLACE_TABS = [
  {
    key: 'marketplace',
    label: 'marketplaceTab.marketplace',
    component: MktHomeTab,
    icon: 'storefront',
    betaTag: true,
  },
  {
    key: 'plans',
    label: 'marketplaceTab.plans',
    component: MktPlanTab,
    icon: 'checklist',
  },
  {
    key: 'additionals',
    label: 'marketplaceTab.additionals',
    component: MktAdditionalsTab,
    icon: 'add_business',
  },
  {
    key: 'addons',
    label: 'marketplaceTab.addons',
    component: MktAddonsTab,
    icon: 'extension',
    children: [
      {
        title: 'marketplaceTab.brand',
        anchorId: `MarketplacePage-BRAND`,
      },
      {
        title: 'marketplaceTab.security',
        anchorId: `MarketplacePage-SECURITY`,
      },
      {
        title: 'marketplaceTab.manage',
        anchorId: `MarketplacePage-MANAGEMENT`,
      },
    ],
  },
  {
    key: 'services',
    label: 'marketplaceTab.services',
    component: MktServicesTab,
    icon: 'business_center',
  },
  {
    key: 'templates',
    label: 'marketplaceTab.templates',
    component: MktTemplatesTab,
    icon: 'widgets',
  },
  {
    key: 'educational',
    label: 'marketplaceTab.educational',
    component: MktEducationalTab,
    icon: 'school',
  },
  {
    key: 'checkout',
    label: 'marketplaceTab.checkout',
    component: CheckoutPage,
    shouldNotRender: () => true,
  },
  {
    key: 'item',
    label: 'marketplaceTab.item',
    component: MarketplaceItemTab,
    shouldNotRender: () => true,
  },
  {
    key: 'templateItem',
    component: MarketplaceTemplateItemTab,
    shouldNotRender: () => true,
  },
];

export function useUserCanActivateWithoutRequest(account, isMasterOfCurrentAccount) {
  const { data: activateWithoutRequest, isLoading: activateWithoutRequestLoading } = useFetchData(async () => {
    if (isMasterOfCurrentAccount) {
      return true;
    }
    return await Api.Marketplace.canActivateAddonWithoutRequest(account?.id);
  }, [account?.id]);

  return { activateWithoutRequest, activateWithoutRequestLoading };
}

export default function MarketplacePage() {
  const context = useBimContext();
  const { selectedItems, selectCard, onClearSelection } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useBimNavigate();

  const { data: availableAccounts = [], isLoading: availableAccountsLoading } = useFetchData(async () => {
    try {
      const accounts = await Api.Account.findAvailable({ includeRelated: true, activeForBilling: true });

      if (accounts.length === 0) {
        navigate('/errors/403');
        return [];
      }

      const accountOpts = accounts.map((account) => {
        return {
          value: account.id,
          label: account.name,
          isMaster: account.accountMasterId === context.user.id,
          account,
        };
      });

      accountOpts.sort((a, b) => {
        return Utils.Strings.compareIgnoreCase(a.label, b.label);
      });

      return accountOpts;
    } catch (e) {
      console.error('Error on fetch available accounts', e);
      UiMsg.error(context.msg.t('no.account.info'));
    }
  });

  const { data: account, isLoading: accountLoading, reload: reloadAccount } = useFetchData(
    async ({ accountId = null }) => {
      try {
        const currentAccountId =
          (searchParams.has(MKT_ACC_QUERY_PARAM) ? _.parseInt(searchParams.get(MKT_ACC_QUERY_PARAM)) : null) ??
          context.accountId;
        return await Api.Account.findAccount(accountId ?? currentAccountId, { publicDataOnly: true });
      } catch (e) {
        console.error('Error on fetch account', e);
        UiMsg.error(context.msg.t('no.account.info'));
      }
    }
  );

  const {
    data: marketplaceItems = {},
    isLoading: marketplaceItemsLoading,
    reload: reloadMarketplaceItems,
  } = useFetchData(async () => {
    if (!account) {
      return [];
    }
    return await Api.Marketplace.fetchAll(account.id);
  }, [account]);

  const selectAccount = async (accountId) => {
    searchParams.set(MKT_ACC_QUERY_PARAM, accountId);
    setSearchParams(searchParams);
    onClearSelection();
    await reloadAccount({ accountId });
  };

  const isMasterOfCurrentAccount = useMemo(() => {
    return availableAccounts.find((acc) => acc.value === account?.id)?.isMaster;
  }, [account, availableAccounts]);

  const { activateWithoutRequest, activateWithoutRequestLoading } = useUserCanActivateWithoutRequest(
    account,
    isMasterOfCurrentAccount
  );

  return (
    <UiBlocker
      block={marketplaceItemsLoading || !account || accountLoading || activateWithoutRequestLoading}
      className={`MarketplacePage ${styles.MarketplacePageBlocker}`}
    >
      {account && !marketplaceItemsLoading && (
        <NavPanel
          account={account}
          accountOpts={availableAccounts}
          onSelectAccount={selectAccount}
          tabs={MARKETPLACE_TABS}
          defaultTab={MARKETPLACE_TABS[0]}
          accountsLoading={availableAccountsLoading}
          tabProps={{
            marketplaceItems: marketplaceItems,
            selectCard: selectCard,
            selectedItems: selectedItems,
            account,
            accountId: account.id,
            isMasterOfCurrentAccount: isMasterOfCurrentAccount,
            reloadMarketplaceItems,
            activateWithoutRequest,
            activateWithoutRequestLoading,
          }}
        />
      )}
    </UiBlocker>
  );
}
